import { useState } from "react";
import { toast } from "sonner";
import { Package, Loader2, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fmt = (c) => `$${((c ?? 0) / 100).toFixed(0)}`;

/**
 * Bundle purchase cards shown on the coach profile between the hero and the
 * BookingWidget. Renders only when the coach has active bundles; otherwise
 * the section is hidden entirely so single-session-only coaches stay clean.
 */
export default function BundleCards({ bundles = [], coachName, coachSlug, onNeedSignIn }) {
  const { user } = useAuth();
  const [buyingId, setBuyingId] = useState(null);

  if (!bundles || bundles.length === 0) return null;

  const buy = async (bundle) => {
    if (!user?.email) {
      onNeedSignIn?.();
      toast.info("Sign in to purchase a bundle.");
      return;
    }
    setBuyingId(bundle.id);
    try {
      const { data } = await api.post(`/api/bundles/${bundle.id}/checkout`, {
        client_email: user.email,
        client_name: user.name || user.email.split("@")[0],
      });
      if (data.checkout_url) window.location.assign(data.checkout_url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start bundle checkout.");
    } finally {
      setBuyingId(null);
    }
  };

  const savingsFor = (b, singleCents) => {
    if (!singleCents) return null;
    const list = singleCents * b.sessions_count;
    if (list <= b.total_price_cents) return null;
    const pct = Math.round(((list - b.total_price_cents) / list) * 100);
    return pct;
  };

  return (
    <section
      data-testid="coach-bundles"
      className="max-w-[1200px] mx-auto px-6 md:px-10 py-8 md:py-12"
    >
      <div className="eyebrow">§ Bundles</div>
      <h2 className="mt-3 font-display text-2xl md:text-3xl tracking-tight text-slate-900">
        Save when you book multiple sessions
      </h2>
      <p className="mt-2 text-slate-500 max-w-[52ch] text-[14px]">
        Buy a pack upfront — {coachName?.split(" ")[0]} gets paid right away, and each session you book with them redeems one credit automatically.
      </p>
      <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {bundles.map((b) => {
          const per = b.per_session_cents;
          return (
            <li
              key={b.id}
              data-testid={`bundle-card-${b.id}`}
              className="rounded-2xl border border-slate-200 bg-white p-6 flex flex-col justify-between gap-4"
            >
              <div>
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display text-xl text-slate-900">{b.name}</h3>
                  <Package className="w-5 h-5 text-slate-400 shrink-0" />
                </div>
                <div className="mt-4 flex items-baseline gap-1.5">
                  <span className="font-display font-medium text-4xl text-slate-900">{fmt(b.total_price_cents)}</span>
                  <span className="text-slate-500 text-[13px]">total</span>
                </div>
                <p className="mt-1 text-[13px] text-slate-500">
                  {b.sessions_count} sessions · {fmt(per)}/session
                </p>
              </div>
              <button
                type="button"
                onClick={() => buy(b)}
                disabled={buyingId === b.id}
                data-testid={`bundle-buy-${b.id}`}
                className="btn-primary h-11 px-5 rounded-full text-[13px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40"
              >
                {buyingId === b.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Buy bundle <ArrowUpRight className="w-4 h-4" /></>)}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
